import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { apiService } from '../services/apiService';

type Role = { id: number; tenantId?: number | null; name: string; description?: string; accessLevel?: number };
type Permission = { id: number; permissionKey: string; description?: string };

const SuperAdminRbacPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);

  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const selectedRole = useMemo(() => roles.find((r) => r.id === selectedRoleId) || null, [roles, selectedRoleId]);

  const [rolePermissionKeys, setRolePermissionKeys] = useState<string[]>([]);
  const [roleUsers, setRoleUsers] = useState<any[]>([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [newRoleAccessLevel, setNewRoleAccessLevel] = useState('10');
  const [newRoleTenantId, setNewRoleTenantId] = useState('');

  const [userIdsText, setUserIdsText] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [perms, rs] = await Promise.all([
        apiService.adminRbacListPermissions(),
        apiService.adminRbacListRoles({}),
      ]);
      setPermissions(perms || []);
      setRoles(rs || []);
      if (!selectedRoleId && rs?.length) setSelectedRoleId(rs[0].id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load RBAC');
    } finally {
      setLoading(false);
    }
  };

  const loadRoleDetails = async (roleId: number) => {
    setError(null);
    try {
      const [keys, users] = await Promise.all([
        apiService.adminRbacGetRolePermissions(roleId),
        apiService.adminRbacGetRoleUsers(roleId),
      ]);
      setRolePermissionKeys(keys || []);
      setRoleUsers(users || []);
      setUserIdsText((users || []).map((u: any) => u.userId).join('\n'));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load role details');
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    if (selectedRoleId != null) loadRoleDetails(selectedRoleId);
  }, [selectedRoleId]);

  const togglePermission = (key: string) => {
    setRolePermissionKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const savePermissions = async () => {
    if (!selectedRoleId) return;
    setLoading(true);
    setError(null);
    try {
      await apiService.adminRbacSetRolePermissions(selectedRoleId, rolePermissionKeys);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save permissions');
    } finally {
      setLoading(false);
    }
  };

  const saveUsers = async () => {
    if (!selectedRoleId) return;
    setLoading(true);
    setError(null);
    try {
      const ids = userIdsText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean)
        .map((s) => Number(s))
        .filter((n) => Number.isFinite(n));
      await apiService.adminRbacSetRoleUsers(selectedRoleId, ids);
      await loadRoleDetails(selectedRoleId);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to save users');
    } finally {
      setLoading(false);
    }
  };

  const createRole = async () => {
    setLoading(true);
    setError(null);
    try {
      const tenantId = newRoleTenantId.trim() ? Number(newRoleTenantId.trim()) : undefined;
      const accessLevel = newRoleAccessLevel.trim() ? Number(newRoleAccessLevel.trim()) : 10;
      const created = await apiService.adminRbacCreateRole({
        tenantId: Number.isFinite(tenantId as any) ? tenantId : undefined,
        name: newRoleName,
        description: newRoleDesc,
        accessLevel: Number.isFinite(accessLevel) ? accessLevel : 10,
      });
      setCreateOpen(false);
      setNewRoleName('');
      setNewRoleDesc('');
      setNewRoleAccessLevel('10');
      setNewRoleTenantId('');
      await load();
      if (created?.id) setSelectedRoleId(created.id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const deleteRole = async () => {
    if (!selectedRoleId) return;
    setLoading(true);
    setError(null);
    try {
      await apiService.adminRbacDeleteRole(selectedRoleId);
      setSelectedRoleId(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to delete role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <AdminPanelSettingsIcon />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Role &amp; Permission Management (RBAC)
        </Typography>
      </Box>

      {loading && <CircularProgress size={22} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => setCreateOpen(true)} disabled={loading}>Create Role</Button>
          <Button variant="outlined" onClick={load} disabled={loading}>Reload</Button>
          <Button variant="outlined" color="error" onClick={deleteRole} disabled={loading || !selectedRoleId}>Delete Role</Button>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '360px 1fr' }, gap: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Roles</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Tenant</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  selected={r.id === selectedRoleId}
                  onClick={() => setSelectedRoleId(r.id)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.tenantId ?? 'GLOBAL'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
            {selectedRole ? `Role: ${selectedRole.name}` : 'Select a role'}
          </Typography>
          {selectedRole && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Access level: {selectedRole.accessLevel ?? 10} · Tenant: {selectedRole.tenantId ?? 'GLOBAL'}
              </Typography>

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Permissions</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 0.5, mb: 1 }}>
                {permissions.map((p) => (
                  <FormControlLabel
                    key={p.permissionKey}
                    control={
                      <Checkbox
                        checked={rolePermissionKeys.includes(p.permissionKey)}
                        onChange={() => togglePermission(p.permissionKey)}
                      />
                    }
                    label={`${p.permissionKey}${p.description ? ` — ${p.description}` : ''}`}
                  />
                ))}
              </Box>
              <Button variant="contained" onClick={savePermissions} disabled={loading}>Save permissions</Button>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Assign users</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Paste **user IDs** (one per line). This replaces existing assignments for this role.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  value={userIdsText}
                  onChange={(e) => setUserIdsText(e.target.value)}
                  placeholder="123\n456\n789"
                />
                <Button sx={{ mt: 1 }} variant="contained" onClick={saveUsers} disabled={loading}>Save users</Button>

                <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>Current users</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>User ID</TableCell>
                      <TableCell>Tenant</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roleUsers.map((u: any) => (
                      <TableRow key={u.userId}>
                        <TableCell>{u.email || u.name || '-'}</TableCell>
                        <TableCell>{u.userId}</TableCell>
                        <TableCell>{u.tenantId ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Paper>
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create role</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Name" value={newRoleName} onChange={(e) => setNewRoleName(e.target.value)} />
          <TextField label="Description" value={newRoleDesc} onChange={(e) => setNewRoleDesc(e.target.value)} />
          <TextField label="Access level" type="number" value={newRoleAccessLevel} onChange={(e) => setNewRoleAccessLevel(e.target.value)} />
          <TextField label="Tenant ID (optional; blank = global)" value={newRoleTenantId} onChange={(e) => setNewRoleTenantId(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createRole} disabled={loading}>Create</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SuperAdminRbacPage;

